import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { useTranslation } from '@/i18n/LanguageContext';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';
import { useEntityDetailsDialog } from '@/contexts/useEntityDetailsDialog';

interface Props {
  groupId: string;
  groupName?: string;
  children: ReactNode;
  locked?: boolean;
  onFocusProperty: (groupId: string, field: 'name' | 'description' | 'color') => void;
  onToggleCollapse?: (groupId: string) => void;
  onToggleLock?: (groupId: string) => void;
  onDelete?: (groupId: string) => void;
  disabled?: boolean;
}

/**
 * Right-click menu for expanded groups (GroupFrame). Mirrors LayerContextMenu
 * but labels the collapse action "Collapse to layer" since an expanded group
 * becomes a layer card when toggled. Radix's Slot needs a real DOM node to
 * attach its cloned ref + onContextMenu handler to.
 */
export function GroupContextMenu({
  groupId,
  groupName,
  children,
  locked,
  onFocusProperty,
  onToggleCollapse,
  onToggleLock,
  onDelete,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const { openDetails } = useEntityDetailsDialog();

  if (disabled) return <>{children}</>;

  const withDispatch = (action: string, fn: () => void) => () => {
    dispatchOnboardingEvent({ name: 'group:contextActionSelected', target: action });
    fn();
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          onContextMenu={() => {
            dispatchOnboardingEvent({ name: 'group:rightclick', target: groupId });
          }}
        >
          {children}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        {onToggleCollapse && (
          <ContextMenuItem
            data-guide="ctx-collapse-to-layer"
            onSelect={withDispatch('ctx-collapse-to-layer', () => onToggleCollapse(groupId))}
          >
            {t('collapseToLayer') || 'Collapse to layer'}
          </ContextMenuItem>
        )}

        <ContextMenuSeparator />

        <ContextMenuItem
          data-guide="ctx-rename"
          onSelect={withDispatch('ctx-rename', () => onFocusProperty(groupId, 'name'))}
        >
          {t('editName') || t('rename') || 'Edit name'}
        </ContextMenuItem>
        <ContextMenuItem
          data-guide="ctx-color"
          onSelect={withDispatch('ctx-color', () => onFocusProperty(groupId, 'color'))}
        >
          {t('editColor') || t('changeColor') || 'Edit color'}
        </ContextMenuItem>
        <ContextMenuItem
          data-guide="ctx-details"
          onSelect={withDispatch('ctx-details', () => openDetails({
            entityId: groupId, entityType: 'group', entityName: groupName,
          }))}
        >
          {t('details') || 'Details…'}
        </ContextMenuItem>

        {onToggleLock && (
          <ContextMenuItem
            data-guide="ctx-toggle-lock"
            onSelect={withDispatch('ctx-toggle-lock', () => onToggleLock(groupId))}
          >
            {locked
              ? (t('unlockGroup') || 'Unlock group')
              : (t('lockGroup') || 'Lock group')}
          </ContextMenuItem>
        )}

        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              data-guide="ctx-delete"
              onSelect={withDispatch('ctx-delete', () => onDelete(groupId))}
              className="text-destructive focus:text-destructive"
            >
              {t('deleteGroup') || 'Delete group'}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
